import {UNREGISTER_ADMIN_PUSH_SUBSCRIPTION} from 'graphql/mutations/aiHelpdesk';

// Browser helpers for agent Web Push (public/admin-push-sw.js).

export const ADMIN_PUSH_SW_URL = '/admin-push-sw.js';

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/** This browser's push subscription, or null. Never throws. */
export const getPushSubscription = async () => {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    return (await reg?.pushManager.getSubscription()) || null;
  } catch {
    return null;
  }
};

/** VAPID public key (base64url) → the bytes pushManager.subscribe expects. */
export const urlBase64ToUint8Array = base64 => {
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, char => char.charCodeAt(0));
};

/**
 * Message for a failed pushManager.subscribe(). Chromium throws AbortError
 * ("Registration failed - push service error") when the browser itself can't
 * reach its push service: Brave with Google push messaging off (its default),
 * in-app / embedded browsers, or a network blocking it. Not fixable from here,
 * so say what the agent can do.
 */
export const pushSubscribeErrorMessage = (err, isBrave = !!navigator.brave) => {
  if (err?.name !== 'AbortError')
    return err?.message || 'Could not turn on alerts';
  if (isBrave) {
    return 'Brave blocks push alerts by default: turn on "Use Google services for push messaging" in brave://settings/privacy, restart Brave, then try again.';
  }
  return "This browser couldn't reach its push service. Use Chrome, Edge or Firefox (not an in-app browser), and check that a VPN or firewall isn't blocking it.";
};

/**
 * Logout: detach this browser from the agent while the token is still valid,
 * so the next agent on a shared machine doesn't get these alerts. Best-effort
 * and capped at 3 s so a slow gateway can't hold up logging out.
 */
export const unregisterAdminPush = client =>
  Promise.race([
    (async () => {
      const sub = await getPushSubscription();
      if (!sub) return;
      await client
        .mutate({
          mutation: UNREGISTER_ADMIN_PUSH_SUBSCRIPTION,
          variables: {input: {endpoint: sub.endpoint}},
        })
        .catch(() => {});
      await sub.unsubscribe().catch(() => {});
    })(),
    new Promise(resolve => setTimeout(resolve, 3000)),
  ]);
